
import { useState, useRef, useEffect } from 'react';

type Language = 'en' | 'es';

interface LanguageSelectorProps {
  scrolled?: boolean;
  variant?: 'light' | 'dark';
}

export default function LanguageSelector({ scrolled = false, variant = 'light' }: LanguageSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = {
    en: { name: 'English', flag: '🇺🇸' },
    es: { name: 'Español', flag: '🇪🇸' }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    localStorage.setItem('language', currentLang);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: currentLang }));
  }, [currentLang]);

  const handleLanguageChange = (lang: Language) => {
    setCurrentLang(lang);
    setIsOpen(false);
    window.location.reload();
  };

  const textColor = variant === 'dark' 
    ? 'text-white' 
    : scrolled 
      ? 'text-[#0A2463]' 
      : 'text-white';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all ${textColor} hover:bg-white/10 cursor-pointer`}
      >
        <span className="text-base">{languages[currentLang].flag}</span>
        <span className="text-sm font-medium hidden sm:inline">{languages[currentLang].name}</span>
        <i className={`ri-arrow-down-s-line text-sm transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 min-w-[140px]">
          {Object.entries(languages).map(([code, { name, flag }]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code as Language)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer ${
                currentLang === code ? 'bg-[#00B4D8]/10 text-[#00B4D8]' : 'text-gray-700'
              }`}
            >
              <span className="text-base">{flag}</span>
              <span className="font-medium">{name}</span>
              {currentLang === code && <i className="ri-check-line ml-auto"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function useLanguage() {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });

  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<Language>;
      setLanguage(customEvent.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange);
    return () => window.removeEventListener('languageChange', handleLanguageChange);
  }, []);

  return language;
}
